import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
  type AudioVideoObserver,
  type MeetingSession,
  type MeetingSessionStatus,
} from "amazon-chime-sdk-js";
import { ToneMicDevice, isDtmfDigit } from "./dtmf.ts";
import { getToken, initAuth, loginErrorMessage, signIn, signOut } from "./auth.ts";
import { playKeyTone } from "./keytones.ts";
import { addRecent, clearRecents, durationText, loadRecents, whenText } from "./recents.ts";

const $ =<T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const loginEl = $<HTMLElement>("login");
const loginStatusEl = $<HTMLElement>("login-status");
const accountNameEl = $<HTMLElement>("account-name");
const dialerEl = $<HTMLElement>("dialer");
const inCallEl = $<HTMLElement>("in-call");
const destinoInput = $<HTMLInputElement>("destino");
const callBtn = $<HTMLButtonElement>("call");
const hangupBtn = $<HTMLButtonElement>("hangup");
const muteBtn = $<HTMLButtonElement>("mute");
const statusEl = $<HTMLElement>("status");
const timerEl = $<HTMLElement>("timer");
const audioEl = $<HTMLAudioElement>("remote-audio");
const keypadEl = $<HTMLElement>("keypad");
const sentDigitsEl = $<HTMLElement>("sent-digits");
const tabsEl = $<HTMLElement>("tabs");
const tabKeypad = $<HTMLButtonElement>("tab-keypad");
const tabRecents = $<HTMLButtonElement>("tab-recents");
const paneKeypad = $<HTMLElement>("pane-keypad");
const paneRecents = $<HTMLElement>("pane-recents");
const recentsList = $<HTMLUListElement>("recents");
const recentsEmpty = $<HTMLElement>("recents-empty");
const recentsClear = $<HTMLButtonElement>("recents-clear");
const recentRow = $<HTMLTemplateElement>("recent-row");
const profileField = $<HTMLElement>("profile-field");
const profileSelect = $<HTMLSelectElement>("profile");

interface Profile { id: string; name: string; phone: string; brand: string; countries: string[] }

// Último perfil usado en este navegador; si el almacenamiento falla, se ignora.
function loadPref(key: string): string {
  try { return localStorage.getItem(key) ?? ""; } catch { return ""; }
}
function savePref(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* sin almacenamiento */ }
}

// ?debug en la URL: registro detallado en consola y ContactId visible en pantalla.
const DEBUG = new URLSearchParams(location.search).has("debug");
// Cómo se envían los tonos durante la llamada:
//  - "connect" (por defecto): método oficial de AWS, SendMessage audio/dtmf vía el backend.
//  - "inband" (?dtmf=inband): tonos mezclados en el audio del micrófono.
const DTMF_MODE = new URLSearchParams(location.search).get("dtmf") === "inband" ? "inband" : "connect";

let session: MeetingSession | null = null;
let contactId: string | null = null;
let muted = false;
let timer: number | undefined;
let wakeLock: { release(): Promise<void> } | null = null;
let audioUp = false;
let toneMic: ToneMicDevice | null = null;
let dtmfQueue: Promise<unknown> = Promise.resolve();
// Llamada en curso para el historial de Recientes.
let attempt: { number: string; at: number } | null = null;
let audioStartedAt = 0;
// true cuando el servidor usa los perfiles de marcación de Salesforce.
let profilesEnabled = false;

function setStatus(text: string, error = false) {
  statusEl.textContent = text;
  statusEl.classList.toggle("error", error);
}

function showLogin(error = "") {
  loginStatusEl.textContent = error;
  loginEl.hidden = false;
  dialerEl.hidden = true;
}

function showDialer(name: string) {
  accountNameEl.textContent = name;
  loginEl.hidden = true;
  dialerEl.hidden = false;
}

/** Llama al backend con el token de Entra: GET sin cuerpo, POST con cuerpo. */
async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${await getToken()}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `Error ${res.status}`);
  return data as T;
}

function updateCallButton() {
  callBtn.disabled = !destinoInput.value.trim() || (profilesEnabled && !profileSelect.value);
}

// Perfiles de marcación del comercial en Salesforce: deciden el número que ve el cliente.
async function loadProfiles() {
  try {
    const data = await api<{ enabled: boolean; profiles: Profile[] }>("/api/profiles");
    profilesEnabled = data.enabled;
    if (!data.enabled) return;
    profileSelect.replaceChildren(...data.profiles.map((p) => new Option(p.phone ? `${p.name} · ${p.phone}` : p.name, p.id)));
    profileField.hidden = false;
    if (data.profiles.length === 0) {
      setStatus("No tienes perfiles de marcación en Salesforce. Pide que te añadan al grupo de tu equipo.", true);
    } else {
      const last = loadPref("perfil");
      if (data.profiles.some((p) => p.id === last)) profileSelect.value = last;
    }
  } catch (err) {
    profilesEnabled = true;
    setStatus((err as Error).message, true);
  } finally {
    updateCallButton();
  }
}

function showTab(tab: "keypad" | "recents") {
  const recents = tab === "recents";
  paneKeypad.hidden = recents;
  paneRecents.hidden = !recents;
  tabKeypad.setAttribute("aria-selected", String(!recents));
  tabRecents.setAttribute("aria-selected", String(recents));
  if (recents) renderRecents();
}

// Pestaña Recientes: tocar la fila lleva el número al teclado; el botón verde llama.
function renderRecents() {
  const items = loadRecents();
  recentsList.replaceChildren(...items.map((item) => {
    const li = (recentRow.content.cloneNode(true) as DocumentFragment).firstElementChild as HTMLLIElement;
    li.querySelector(".recent-number")!.textContent = item.number;
    const meta = li.querySelector(".recent-meta")!;
    const status = document.createElement("span");
    status.textContent = item.connected ? durationText(item.durationSec) : "No conectada";
    if (!item.connected) status.className = "missed";
    meta.append(`${whenText(item.at)} · `, status);
    li.querySelector<HTMLButtonElement>(".recent-pick")!.addEventListener("click", () => {
      destinoInput.value = item.number;
      updateCallButton();
      showTab("keypad");
    });
    const callAgain = li.querySelector<HTMLButtonElement>(".recent-call")!;
    callAgain.setAttribute("aria-label", `Llamar a ${item.number}`);
    callAgain.addEventListener("click", () => {
      destinoInput.value = item.number;
      showTab("keypad");
      call();
    });
    return li;
  }));
  recentsEmpty.hidden = items.length > 0;
  recentsClear.hidden = items.length === 0;
}

/** Guarda el intento en Recientes (conectada con su duración, o «No conectada»). */
function recordAttempt() {
  if (!attempt) return;
  const connected = audioStartedAt > 0;
  addRecent({
    number: attempt.number,
    at: attempt.at,
    durationSec: connected ? Math.floor((Date.now() - audioStartedAt) / 1000) : 0,
    connected,
  });
  attempt = null;
  audioStartedAt = 0;
}

function startTimer() {
  const start = Date.now();
  timerEl.textContent = "00:00";
  timer = window.setInterval(() => {
    const s = Math.floor((Date.now() - start) / 1000);
    timerEl.textContent = `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  }, 1000);
}

async function keepScreenOn() {
  try {
    wakeLock = await (navigator as any).wakeLock?.request("screen") ?? null;
  } catch { /* no soportado o denegado */ }
}

function resetUi(message: string, error = false) {
  window.clearInterval(timer);
  wakeLock?.release().catch(() => {});
  wakeLock = null;
  session = null;
  contactId = null;
  audioUp = false;
  toneMic = null;
  dtmfQueue = Promise.resolve();
  sentDigitsEl.hidden = true;
  sentDigitsEl.textContent = "";
  muted = false;
  muteBtn.classList.remove("on");
  muteBtn.setAttribute("aria-pressed", "false");
  inCallEl.hidden = true;
  callBtn.hidden = false;
  destinoInput.disabled = false;
  profileSelect.disabled = false;
  updateCallButton();
  tabsEl.hidden = false;
  setStatus(message, error);
}

async function call() {
  const destino = destinoInput.value.trim();
  if (!destino) { setStatus("Escribe un número", true); return; }
  if (profilesEnabled && !profileSelect.value) { setStatus("Elige con qué perfil llamar", true); return; }

  callBtn.disabled = true;
  destinoInput.disabled = true;
  profileSelect.disabled = true;
  // Durante la llamada se queda en el teclado (DTMF).
  showTab("keypad");
  tabsEl.hidden = true;
  setStatus("Pidiendo permiso de micrófono…");

  try {
    // Pedir el micrófono antes de crear la llamada en Connect.
    const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
    probe.getTracks().forEach((t) => t.stop());
  } catch {
    resetUi("Sin permiso de micrófono. Actívalo en los ajustes del navegador.", true);
    return;
  }

  setStatus("Conectando…");
  attempt = { number: destino, at: Date.now() };
  audioStartedAt = 0;
  let data: { contactId: string; meeting: unknown; attendee: unknown };
  try {
    data = await api("/api/call", { destino, profileId: profilesEnabled ? profileSelect.value : undefined });
  } catch (err) {
    recordAttempt();
    resetUi((err as Error).message, true);
    return;
  }
  contactId = data.contactId;
  // La llamada ya existe en Connect: se puede colgar aunque el audio no haya arrancado.
  inCallEl.hidden = false;
  callBtn.hidden = true;
  timerEl.textContent = "00:00";

  const step = (text: string) => setStatus(DEBUG ? `${text} [${data.contactId}]` : text);
  if (DEBUG) console.info("StartWebRTCContact", data);

  let meetingSession: DefaultMeetingSession;
  try {
    const logger = new ConsoleLogger("softphone", DEBUG ? LogLevel.INFO : LogLevel.WARN);
    const deviceController = new DefaultDeviceController(logger);
    const config = new MeetingSessionConfiguration(data.meeting, data.attendee);
    meetingSession = new DefaultMeetingSession(config, logger, deviceController);
  } catch (err) {
    // Sin sesión de audio no hay llamada: colgar también en Connect.
    console.error(err);
    endCall(`No se ha podido preparar el audio: ${(err as Error)?.message ?? err}`, true);
    return;
  }
  session = meetingSession;
  const av = meetingSession.audioVideo;
  let started = false;

  const observer: AudioVideoObserver = {
    audioVideoDidStartConnecting: (reconnecting: boolean) => {
      step(reconnecting ? "Reconectando audio…" : "Conectando audio…");
    },
    audioVideoDidStart: () => {
      started = true;
      audioUp = true;
      audioStartedAt ||= Date.now();
      sentDigitsEl.hidden = false;
      step(`En llamada con ${destino}`);
      startTimer();
      keepScreenOn();
    },
    audioVideoDidStop: (status: MeetingSessionStatus) => {
      if (session !== meetingSession) return; // ya finalizada con Colgar
      const code = status.statusCode();
      console.info("llamada terminada", code, MeetingSessionStatusCode[code]);
      // Left: colgamos nosotros; MeetingEnded / AudioAttendeeRemoved: Connect cerró la llamada.
      const normal = [
        MeetingSessionStatusCode.Left,
        MeetingSessionStatusCode.MeetingEnded,
        MeetingSessionStatusCode.AudioAttendeeRemoved,
      ].includes(code);
      endCall(normal ? "Llamada finalizada" : `Llamada interrumpida (${MeetingSessionStatusCode[code]})`, !normal);
    },
  };
  av.addObserver(observer);

  // Si el audio no llega a conectar, no dejar al comercial esperando para siempre.
  window.setTimeout(() => {
    if (session === meetingSession && !started) {
      endCall("No se ha podido conectar el audio. Prueba con otra red (p. ej. datos móviles).", true);
    }
  }, 30_000);

  try {
    step("Abriendo micrófono…");
    const mics = await av.listAudioInputDevices();
    const micId = mics[0]?.deviceId ?? "default";
    if (DTMF_MODE === "inband") {
      toneMic = new ToneMicDevice(micId);
      await av.startAudioInput(toneMic);
    } else {
      await av.startAudioInput(micId);
    }
    await av.bindAudioElement(audioEl);
    step("Conectando audio…");
    av.start();
  } catch (err) {
    console.error(err);
    endCall(`No se ha podido abrir el audio: ${(err as Error)?.message ?? err}`, true);
  }
}

// Libera micrófono y altavoz y cuelga en Connect. Se llama siempre que termina la
// llamada, sea por Colgar, por el destino o por un corte de red, para que el
// contacto no siga vivo en Connect sin nadie al otro lado.
async function endCall(message: string, error = false) {
  const s = session;
  const id = contactId;
  const tones = toneMic;
  if (!s && !id) return;
  session = null;
  contactId = null;
  recordAttempt();
  resetUi(message, error);
  if (s) {
    try { s.audioVideo.stop(); } catch { /* ya parada */ }
    try { await s.audioVideo.stopAudioInput(); } catch { /* sin entrada */ }
    try { s.audioVideo.unbindAudioElement(); } catch { /* sin salida */ }
    try { await s.deviceController.destroy(); } catch { /* ya destruido */ }
  }
  try { await tones?.stop(); } catch { /* ya parado */ }
  // Si Connect ya cerró el contacto, el servidor ignora el error.
  if (id) await api("/api/hangup", { contactId: id }).catch(() => {});
}

function hangup() {
  endCall("Llamada finalizada");
}

// Teclado: antes de llamar escribe el número; con la llamada en curso envía tonos DTMF.
function pressKey(digit: string) {
  if (session && audioUp) {
    sendDigit(digit);
  } else if (!contactId && !destinoInput.disabled) {
    destinoInput.value += digit;
  }
}

function sendDigit(digit: string) {
  sentDigitsEl.textContent = (sentDigitsEl.textContent ?? "") + digit;
  if (DTMF_MODE === "inband") {
    toneMic?.play(digit);
    return;
  }
  const id = contactId;
  // En cola para que los dígitos lleguen en el orden en que se pulsan.
  dtmfQueue = dtmfQueue
    .then(() => api("/api/dtmf", { contactId: id, digits: digit }))
    .catch((err) => {
      if (contactId === id) setStatus((err as Error).message, true);
    });
}

function toggleMute() {
  if (!session) return;
  muted = !muted;
  if (muted) session.audioVideo.realtimeMuteLocalAudio();
  else session.audioVideo.realtimeUnmuteLocalAudio();
  muteBtn.classList.toggle("on", muted);
  muteBtn.setAttribute("aria-pressed", String(muted));
}

$<HTMLButtonElement>("sign-in").addEventListener("click", () => {
  signIn().catch((err) => showLogin(loginErrorMessage(err)));
});
$<HTMLButtonElement>("sign-out").addEventListener("click", () => {
  if (contactId) return; // no cerrar sesión con una llamada en curso
  signOut().catch(() => {});
});
callBtn.addEventListener("click", call);
hangupBtn.addEventListener("click", hangup);
muteBtn.addEventListener("click", toggleMute);
destinoInput.addEventListener("keydown", (e) => { if (e.key === "Enter" && destinoInput.value.trim()) call(); });
destinoInput.addEventListener("input", updateCallButton);
keypadEl.addEventListener("click", (e) => {
  const digit = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-digit]")?.dataset.digit;
  if (!digit) return;
  playKeyTone(digit);
  pressKey(digit);
  if (!contactId) updateCallButton();
});
// En el ordenador, las teclas 0-9 * # también envían tonos durante la llamada.
document.addEventListener("keydown", (e) => {
  if (session && audioUp && isDtmfDigit(e.key) && !e.repeat) {
    playKeyTone(e.key);
    sendDigit(e.key);
  }
});
tabKeypad.addEventListener("click", () => showTab("keypad"));
tabRecents.addEventListener("click", () => showTab("recents"));
profileSelect.addEventListener("change", () => { savePref("perfil", profileSelect.value); updateCallButton(); });
recentsClear.addEventListener("click", () => { clearRecents(); renderRecents(); });

// Permite abrir la web con ?tel=612345678 (p. ej. desde un enlace en Salesforce).
const tel = new URLSearchParams(location.search).get("tel");
if (tel) destinoInput.value = tel;
updateCallButton();

initAuth()
  .then((account) => {
    if (!account) return showLogin();
    showDialer(account.name || account.username);
    return loadProfiles();
  })
  .catch((err) => {
    console.error(err);
    showLogin(loginErrorMessage(err));
  });
